import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../components/AuthContext";

const QUICK_ACTIONS = [
  { label: "🔍 Analyze my active loads", query: "Analyze all my active loads and identify any that are at risk due to weather or delays. Recommend actions for each." },
  { label: "🌤️ Check route conditions", query: "Check weather and traffic conditions for routes from Texas to California. Are there any issues I should be aware of?" },
  { label: "🚂 Find intermodal savings", query: "Which of my current loads could benefit from intermodal shipping? Calculate the cost and CO2 savings." },
  { label: "📊 Optimize capacity", query: "Analyze my network capacity for the Southwest region this week. Find consolidation and backhaul opportunities." },
  { label: "🚛 Auto-dispatch a load", query: "Show me my posted loads that need carriers assigned. Recommend the best carrier for each based on reliability and cost." },
  { label: "⚠️ Run proactive monitoring", query: "Run a full proactive monitoring check. Identify all weather alerts, rail delays, and loads at risk." }
];

export default function FreightAgent() {
  const { adminEmail } = useAuth();
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: `🤖 **Autonomous Freight Agent Ready**

I'm your AI-powered freight operations agent. I can:

- **Analyze & Dispatch** - Find optimal carriers, auto-dispatch loads
- **Monitor & Alert** - Track weather, traffic, rail delays in real-time  
- **Reroute & Optimize** - Suggest alternatives to avoid disruptions
- **Book Intermodal** - Auto-book rail legs for cost & CO₂ savings
- **Optimize Capacity** - Find consolidation & backhaul opportunities

I'll check real-time conditions and quantify savings for every recommendation.

**What would you like me to analyze?**`
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conditions, setConditions] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [showConditions, setShowConditions] = useState(false);
  const messagesEndRef = useRef(null);

  const headers = { 
    'Content-Type': 'application/json',
    'x-admin-email': adminEmail, 
    'x-user-role': 'BROKER_ADMIN' 
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    loadConditions();
    loadMonitoring();
  }, []);

  async function loadConditions() {
    try {
      const res = await fetch('/api/freight-agent/conditions', { headers });
      const data = await res.json();
      setConditions(data);
    } catch (e) { console.error('Conditions error:', e); }
  }

  async function loadMonitoring() {
    try {
      const res = await fetch('/api/freight-agent/monitoring', { headers });
      const data = await res.json();
      setMonitoring(data);
    } catch (e) { console.error('Monitoring error:', e); }
  }

  async function sendMessage(text) {
    if (!text.trim()) return;
    
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/freight-agent/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: text, sessionId })
      });
      
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      
      setSessionId(data.sessionId);
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response,
        toolsUsed: data.toolsUsed,
        iterations: data.iterations
      }]);
      
    } catch (e) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `❌ Error: ${e.message}`,
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  }

  const alertCount = monitoring?.summary?.totalAlerts || 0;
  const highAlerts = monitoring?.summary?.highSeverity || 0;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 28 }}>🤖</span> Autonomous Freight Agent
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 14 }}>
            AI-powered dispatching, rerouting & optimization
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {alertCount > 0 && (
            <div style={{ 
              padding: '8px 16px', 
              background: highAlerts > 0 ? '#fee2e2' : '#fef3c7',
              border: `1px solid ${highAlerts > 0 ? '#fecaca' : '#fde68a'}`,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ color: highAlerts > 0 ? '#dc2626' : '#d97706', fontWeight: 700 }}>
                {highAlerts > 0 ? '🚨' : '⚠️'} {alertCount} Active Alerts
              </span>
            </div>
          )}
          <button 
            onClick={() => setShowConditions(!showConditions)}
            style={{ padding: '8px 16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >
            {showConditions ? '✕ Hide' : '📡 Live Conditions'}
          </button>
        </div>
      </div>

      {/* Live Conditions Panel */}
      {showConditions && conditions && (
        <div style={{ background: '#111827', borderRadius: 12, padding: 16, marginBottom: 16, color: 'white' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: 14, opacity: 0.8 }}>📡 LIVE CONDITIONS</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {/* Weather */}
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>🌤️ Weather Alerts</div>
              {Object.entries(conditions.weather || {})
                .filter(([_, data]) => data.alerts?.length > 0)
                .slice(0, 3)
                .map(([state, data]) => (
                  <div key={state} style={{ fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#f87171' }}>⚠️ {state}:</span> {data.alerts[0]}
                  </div>
                ))}
              {Object.values(conditions.weather || {}).every(d => !d.alerts?.length) && (
                <div style={{ fontSize: 12, color: '#4ade80' }}>✓ No active alerts</div>
              )}
            </div>

            {/* Traffic */}
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>🚗 Traffic</div>
              {Object.entries(conditions.traffic || {})
                .filter(([_, data]) => data.congestion === 'heavy')
                .map(([route, data]) => (
                  <div key={route} style={{ fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#f87171' }}>{route}:</span> Heavy ({data.avgSpeed} mph)
                  </div>
                ))}
              {Object.values(conditions.traffic || {}).every(d => d.congestion !== 'heavy') && (
                <div style={{ fontSize: 12, color: '#4ade80' }}>✓ Traffic flowing</div>
              )}
            </div>

            {/* Rail */}
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>🚂 Rail Corridors</div>
              {Object.entries(conditions.railCorridors || {})
                .filter(([_, data]) => data.delays > 4)
                .slice(0, 2)
                .map(([id, data]) => (
                  <div key={id} style={{ fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#fbbf24' }}>{data.name.split(' ')[0]}:</span> +{data.delays}h delay
                  </div>
                ))}
              {Object.values(conditions.railCorridors || {}).every(d => d.delays <= 4) && (
                <div style={{ fontSize: 12, color: '#4ade80' }}>✓ On schedule</div>
              )}
            </div>

            {/* Intermodal */}
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>📦 Intermodal</div>
              {Object.entries(conditions.intermodalTerminals || {}).slice(0, 2).map(([id, data]) => (
                <div key={id} style={{ fontSize: 12, marginBottom: 4 }}>
                  {data.name.split(' ')[0]}: {data.available} slots, next: {data.nextDeparture}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16 }}>
        {/* Chat Area */}
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 280px)' }}>
          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 12 }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ 
                display: 'flex', 
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 16
              }}>
                <div style={{ 
                  maxWidth: '85%',
                  padding: 16,
                  borderRadius: 12,
                  background: msg.role === 'user' ? '#3b82f6' : msg.isError ? '#fee2e2' : '#f3f4f6',
                  color: msg.role === 'user' ? 'white' : '#111827'
                }}>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {msg.content.split('**').map((part, j) => 
                      j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                    )}
                  </div>
                  {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.1)', fontSize: 11, color: '#6b7280' }}>
                      🔧 Analyzed: {msg.toolsUsed.map(t => t.tool.replace(/_/g, ' ')).join(' → ')}
                      {msg.iterations && ` (${msg.iterations} steps)`}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16 }}>
                <div style={{ padding: 16, borderRadius: 12, background: '#f3f4f6' }}>
                  <span>🤖 Analyzing conditions and calculating options...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ display: 'flex', gap: 12 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
              placeholder="Ask me to analyze loads, check conditions, find savings, or optimize routes..."
              disabled={loading}
              style={{ flex: 1, padding: 16, borderRadius: 12, border: '2px solid #e5e7eb', fontSize: 15, outline: 'none' }}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim()}
              style={{ padding: '16px 24px', background: loading ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? '...' : 'Send'}
            </button>
          </div>
        </div>

        {/* Quick Actions Sidebar */}
        <div>
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700 }}>⚡ Quick Actions</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              {QUICK_ACTIONS.map((action, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(action.query)}
                  disabled={loading}
                  style={{ 
                    padding: 12, 
                    background: '#f9fafb', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: 8, 
                    textAlign: 'left',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    fontWeight: 500
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {/* Monitoring Summary */}
          {monitoring && monitoring.alerts?.length > 0 && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 16, marginTop: 16 }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700, color: '#991b1b' }}>
                🚨 Active Alerts ({monitoring.alerts.length})
              </h3>
              {monitoring.alerts.slice(0, 3).map((alert, i) => (
                <div key={i} style={{ fontSize: 12, marginBottom: 8, paddingBottom: 8, borderBottom: i < 2 ? '1px solid #fecaca' : 'none' }}>
                  <div style={{ fontWeight: 600, color: '#dc2626' }}>{alert.type.replace(/_/g, ' ').toUpperCase()}</div>
                  <div style={{ color: '#7f1d1d' }}>{alert.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
